// <div data-controller="notes--publish">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import helpers from "@/utils/helpers";
import { createFlash } from "@/utils/flash";

export default class extends Controller {
  static targets = [
    "emailTitle", "dateTime", "priceContainer", "memberPriceInput",
    "priceInput",
    "publishingRecipientType", "publishingPostingType",
    "sendMailContainer",
    "emailTitleInput", "emailTitleWarn", "hiddenSendTestSubmit",
    "previewDataContainer", "adultOnlyCheckBox",
    "previewModeRadio", "previewRangePanel", "previewCustomPanel",
    "previewBlocksCountInput", "previewBlocksCountLabel", "previewRangeBlock",
    "webVisibilityContainer",
    "senderEmailAddressContainer", "senderEmailAddressSelect",
    "subscriptionGroupContainer", "subscriptionGroupSelect",
    "recipientContainer", "recipientDescription",
    "marketingEnabledRecipientContainer",
    "delayedAccessConfigPanel", "delayedAccessTargetType",
    "delayedAccessDays", "enableDelayedAccessCheckbox",
    "delayedAccessExplanation",
    "spamCheckerContainer", "spamCheckerIdle", "spamCheckerLoading",
    "spamCheckerSafe", "spamCheckerWarning", "spamCheckerError", "spamRiskList",
    "abTestWinnerOptions",
    "clickTrackingSelect", "clickTrackingMailyWarning",
    "sendModeRadio", "immediateInfo", "schedulePanel",
    "scheduleInfo", "scheduleInfoIcon", "scheduleTimeText", "submitButton",
    "sendDatetimeField", "pastTimeWarning",
    "warmingOptionPanel", "warmingRadio", "warmingScheduleText"
  ];

  static values = {
    publishingRecipientTypesI18n: Object,
    spaceUrl: String,
    postExtId: String
  }

  onEmailTitleChange(e) {
    this.emailTitleTarget.innerText = e.target.value;
  }

  // flatpickr 값은 "2026-06-29 14:00 (월요일 오후 2:00)" 형태라 브라우저에 따라
  // new Date() 가 Invalid Date 를 반환한다. 앞부분 "YYYY-MM-DD HH:MM" 만 뽑아 파싱.
  parseSendDatetime(val) {
    if (!val) return null;
    const m = String(val).match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return null;
    const date = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    return isNaN(date.getTime()) ? null : date;
  }

  onDateTimeChange(e) {
    const date = this.parseSendDatetime(e.target.value);
    if (!date) return;

    // 24시간 포맷 옵션
    const options24 = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false // 24시간 포맷
    };

    // 12시간 포맷 옵션
    const options12 = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true // 12시간 포맷
    };

    // 포맷터 생성
    const formatter24 = new Intl.DateTimeFormat('ko-KR', options24);
    const formatter12 = new Intl.DateTimeFormat('ko-KR', options12);

    // 포맷팅된 24시간제 날짜 및 시간
    const parts24 = formatter24.formatToParts(date);
    const year = parts24.find(p => p.type === 'year').value;
    const month = parts24.find(p => p.type === 'month').value;
    const day = parts24.find(p => p.type === 'day').value;
    const weekday = parts24.find(p => p.type === 'weekday').value;
    const hour24 = parts24.find(p => p.type === 'hour').value;
    const minute = parts24.find(p => p.type === 'minute').value;

    // 포맷팅된 12시간제 시간 및 오전/오후
    const parts12 = formatter12.formatToParts(date);
    const hour12 = parts12.find(p => p.type === 'hour').value;
    const dayPeriod = parts12.find(p => p.type === 'dayPeriod').value;

    // 최종 포맷 조합
    const formattedDate = `${year}-${month}-${day} ${hour24}:${minute} (${weekday} ${dayPeriod} ${hour12}:${minute})`;

    this.dateTimeTarget.innerText = formattedDate;

    // 예약 발행 모드에서 시간 안내 및 버튼 업데이트
    this.updateScheduleInfo(date);
    this.updateSubmitButton();
  }

  get selectedSendMode() {
    if (!this.hasSendModeRadioTarget) return "scheduled";
    const checked = this.sendModeRadioTargets.find(r => r.checked);
    return checked ? checked.value : "immediate";
  }

  get formattedScheduleTime() {
    if (!this.hasSendDatetimeFieldTarget) return "";
    const val = this.sendDatetimeFieldTarget.value;
    if (!val) return "";
    const date = this.parseSendDatetime(val);
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d} ${h}:${min}`;
  }

  onSendModeChange() {
    const isScheduled = this.selectedSendMode === "scheduled";

    if (this.hasImmediateInfoTarget) {
      this.immediateInfoTarget.classList.toggle("hidden", isScheduled);
    }
    if (this.hasSchedulePanelTarget) {
      this.schedulePanelTarget.classList.toggle("hidden", !isScheduled);
    }

    // send_datetime 필드 required 토글
    if (this.hasSendDatetimeFieldTarget) {
      this.sendDatetimeFieldTarget.required = isScheduled;
    }

    this.updateSubmitButton();
  }

  updateScheduleInfo(date) {
    if (!this.hasScheduleTimeTextTarget) return;

    if (date && !isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      this.scheduleTimeTextTarget.textContent = `${y}-${m}-${d} ${h}:${min}에 발송 예정입니다`;

      // 시간 선택 완료: 붉은색 → 초록색 전환
      this.setScheduleInfoStyle("green");

      // 과거 시간 경고
      if (this.hasPastTimeWarningTarget) {
        const isPast = date < new Date();
        this.pastTimeWarningTarget.classList.toggle("hidden", !isPast);
      }
    }
  }

  setScheduleInfoStyle(color) {
    if (!this.hasScheduleInfoTarget) return;

    const info = this.scheduleInfoTarget;
    const text = this.scheduleTimeTextTarget;
    const icon = this.hasScheduleInfoIconTarget ? this.scheduleInfoIconTarget : null;

    // Tailwind 퍼지 대응: 전체 클래스명을 명시적으로 사용
    const redClasses = {
      infoBg: ["bg-red-50", "dark:bg-red-900/20", "border-red-200", "dark:border-red-800"],
      text: ["text-red-700", "dark:text-red-300"],
      icon: ["text-red-500"]
    };
    const greenClasses = {
      infoBg: ["bg-green-50", "dark:bg-green-900/20", "border-green-200", "dark:border-green-800"],
      text: ["text-green-700", "dark:text-green-300"],
      icon: ["text-green-500"]
    };

    const add = color === "green" ? greenClasses : redClasses;
    const remove = color === "green" ? redClasses : greenClasses;

    info.classList.remove(...remove.infoBg);
    info.classList.add(...add.infoBg);

    text.classList.remove(...remove.text);
    text.classList.add(...add.text);

    if (icon) {
      icon.classList.remove(...remove.icon);
      icon.classList.add(...add.icon);
    }
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;

    const btn = this.submitButtonTarget;
    if (this.selectedSendMode === "scheduled") {
      const time = this.formattedScheduleTime;
      btn.value = "예약 발행하기";
      btn.dataset.confirm = time
        ? `${time}에 발행이 예약됩니다. 진행하시겠어요?`
        : "예약 발행을 진행하시겠어요?";
    } else {
      btn.value = "지금 발행하기";
      btn.dataset.confirm = "지금 바로 발행됩니다. 진행하시겠어요?";
    }
  }


  checkEmailTitleAd(e) {
    if (e.target.value.includes("광고")) {
      this.emailTitleWarnTarget.innerText = "뉴스레터 제목에 '광고' 라는 단어가 포함되어 있네요. 실제로 광고가 포함되어 있으시다면 광고 문구를 반드시 넣어야 하지만, 그렇지 않은 경우에 넣으시면 광고가 아님에도 이메일이 프로모션 함으로 분류 될 가능성이 있습니다. 또한 법적으로 광고성 이메일의 경우에는 명시적으로 광고 수신에 동의하신 분들에게만 보낼 수 있습니다. 광고 수신 동의 기능의 경우 '브랜딩 플랜' 이상 부터 사용할 수 있으며, 수신 동의 하지 않은 사람들에게 광고를 보낼 경우 생기는 책임은 개별 뉴스레터에 있습니다.";
    } else {
      this.emailTitleWarnTarget.innerText = "";
    }
  }

  sendTest = (e) => {
    try {
      const clickedCommand = window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey;
      if (clickedCommand) {
        return;
      }
    } catch {
    }

    this.hiddenSendTestSubmitTarget.click();
  }

  get selectedPreviewMode() {
    if (!this.hasPreviewModeRadioTarget) return "range";
    const checked = this.previewModeRadioTargets.find(r => r.checked);
    return checked ? checked.value : "range";
  }

  onPreviewModeChange = () => {
    if (!this.hasPreviewRangePanelTarget || !this.hasPreviewCustomPanelTarget) return;
    const mode = this.selectedPreviewMode;
    this.previewRangePanelTarget.classList.toggle("hidden", mode !== "range");
    this.previewCustomPanelTarget.classList.toggle("hidden", mode !== "custom");
    if (mode === "range") this.applyPreviewBlocksCount();
  }

  onPreviewBlocksCountChange = () => {
    this.applyPreviewBlocksCount();
  }

  applyPreviewBlocksCount = () => {
    if (!this.hasPreviewBlocksCountInputTarget) return;
    const input = this.previewBlocksCountInputTarget;
    const total = this.previewRangeBlockTargets.length;
    let n = parseInt(input.value, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (total > 0 && n > total) n = total;
    input.value = n;

    this.previewRangeBlockTargets.forEach((el, idx) => {
      el.classList.toggle("hidden", idx >= n);
    });

    if (this.hasPreviewBlocksCountLabelTarget) {
      this.previewBlocksCountLabelTarget.textContent = String(n);
    }
  }

  get checkedPublishingRecipientType() {
    const postingTypeRadio = document.querySelector('input[name="post[publishing_recipient_type]"]:checked')
    return postingTypeRadio && postingTypeRadio.value;
  }

  get checkedPublishingRecipientTypeDescription() {
    const postingTypeRadio = document.querySelector('input[name="post[publishing_recipient_type]"]:checked')
    return postingTypeRadio && postingTypeRadio.dataset.description;
  }

  get checkedPublishingPostingType() {
    const postingTypeRadio = document.querySelector('input[name="post[publishing_posting_type]"]:checked')
    return postingTypeRadio && postingTypeRadio.value;
  }

  get isAdultOnly() {
    return this.hasAdultOnlyCheckBoxTarget && this.adultOnlyCheckBoxTarget.checked;
  }

  onPublishingTypeBlur() {
    // Handle product type pricing
    if (this.checkedPublishingPostingType === "product") {
      this.priceContainerTarget.classList.remove("hidden")
      this.priceInputTarget.required = true;
      if (this.hasMemberPriceInputTarget) {
        this.memberPriceInputTarget.required = true;
      }
    } else {
      // Check if delayed access is enabled and target is product
      const isDelayedAccessEnabled = this.hasEnableDelayedAccessCheckboxTarget &&
                                     this.enableDelayedAccessCheckboxTarget.checked;
      const selectedTarget = document.querySelector('input[name="delayed_access_target_type"]:checked');
      const targetIsProduct = selectedTarget && selectedTarget.value === "product";

      if (isDelayedAccessEnabled && targetIsProduct) {
        // Keep price container visible if delayed access target is product
        this.priceContainerTarget.classList.remove("hidden");
        this.priceInputTarget.required = true;
        if (this.hasMemberPriceInputTarget) {
          this.memberPriceInputTarget.required = true;
        }
      } else {
        this.priceContainerTarget.classList.add("hidden")
        this.priceInputTarget.required = false;
        if (this.hasMemberPriceInputTarget) {
          this.memberPriceInputTarget.required = false;
        }
      }
    }

    // Update explanation when posting type changes
    this.updateDelayedAccessExplanation();

    if (this.checkedPublishingRecipientType === "none") {
      this.sendMailContainerTargets.forEach((target) => {
        target.classList.add("hidden")
      })
      this.senderEmailAddressContainerTarget.classList.add("hidden")
      this.webVisibilityContainerTarget.classList.add("hidden")
      this.recipientContainerTarget.classList.add("hidden");
    } else {
      this.sendMailContainerTargets.forEach((target) => {
        target.classList.remove("hidden")
      })
      this.senderEmailAddressContainerTarget.classList.remove("hidden")
      this.webVisibilityContainerTarget.classList.remove("hidden")
      this.recipientContainerTarget.classList.remove("hidden");
    }

    // Show preview for restricted types
    const needsPreview = ["product", "membership_only", "subscriber_only"].includes(this.checkedPublishingPostingType);

    // Also check if delayed access is enabled with restricted target type
    const isDelayedAccessEnabled = this.hasEnableDelayedAccessCheckboxTarget &&
                                   this.enableDelayedAccessCheckboxTarget.checked;
    const selectedTarget = document.querySelector('input[name="delayed_access_target_type"]:checked');
    const targetNeedsPreview = selectedTarget &&
                              ["product", "membership_only", "subscriber_only"].includes(selectedTarget.value);

    if (needsPreview || (isDelayedAccessEnabled && targetNeedsPreview) || this.isAdultOnly) {
      this.previewDataContainerTarget.classList.remove("hidden")
      this.onPreviewModeChange();
    } else {
      this.previewDataContainerTarget.classList.add("hidden")
    }

    if (this.hasSubscriptionGroupContainerTarget) {
      if (this.checkedPublishingRecipientType === "subscription_group") {
        this.subscriptionGroupContainerTarget.classList.remove("hidden");
        if (this.hasSubscriptionGroupSelectTarget) {
          this.subscriptionGroupSelectTarget.required = true;
        }
      } else {
        this.subscriptionGroupContainerTarget.classList.add("hidden");
        if (this.hasSubscriptionGroupSelectTarget) {
          this.subscriptionGroupSelectTarget.required = false;
        }
      }
    }

    if (this.hasMarketingEnabledRecipientContainerTarget) {
      if (this.checkedPublishingRecipientType === "marketing_enabled") {
        this.marketingEnabledRecipientContainerTarget.classList.remove("hidden");
      } else {
        this.marketingEnabledRecipientContainerTarget.classList.add("hidden");
      }
    }

    if (this.checkedPublishingRecipientTypeDescription) {
      this.recipientDescriptionTarget.innerHTML = `✉️&nbsp;&nbsp;${this.checkedPublishingRecipientTypeDescription}`;
    }

    this.updateWarmingOption();
  }

  toggleDelayedAccessConfig() {
    if (this.hasEnableDelayedAccessCheckboxTarget && this.enableDelayedAccessCheckboxTarget.checked) {
      this.delayedAccessConfigPanelTarget.classList.remove("hidden");
      this.updateDelayedAccessExplanation();
      this.checkDelayedAccessTargetType();
    } else {
      this.delayedAccessConfigPanelTarget.classList.add("hidden");
      // Re-evaluate pricing visibility when disabled
      this.onPublishingTypeBlur();
    }
  }

  onDelayedAccessTargetChange() {
    this.checkDelayedAccessTargetType();
    this.updateDelayedAccessExplanation();
  }

  checkDelayedAccessTargetType() {
    const selectedTarget = document.querySelector('input[name="delayed_access_target_type"]:checked');
    const isDelayedAccessEnabled = this.hasEnableDelayedAccessCheckboxTarget &&
                                   this.enableDelayedAccessCheckboxTarget.checked;

    if (!isDelayedAccessEnabled) return;

    // Show price container if EITHER:
    // 1. Current posting type is product, OR
    // 2. Target type is product (for delayed access)
    const currentIsProduct = this.checkedPublishingPostingType === "product";
    const targetIsProduct = selectedTarget && selectedTarget.value === "product";

    if (currentIsProduct || targetIsProduct) {
      this.priceContainerTarget.classList.remove("hidden");
      this.priceInputTarget.required = true;
      if (this.hasMemberPriceInputTarget) {
        this.memberPriceInputTarget.required = true;
      }
    } else {
      this.priceContainerTarget.classList.add("hidden");
      this.priceInputTarget.required = false;
      if (this.hasMemberPriceInputTarget) {
        this.memberPriceInputTarget.required = false;
      }
    }

    // Show preview data if either current or target requires it
    const currentNeedsPreview = ["product", "membership_only", "subscriber_only"].includes(this.checkedPublishingPostingType);
    const targetNeedsPreview = selectedTarget && ["product", "membership_only", "subscriber_only"].includes(selectedTarget.value);

    if (currentNeedsPreview || targetNeedsPreview || this.isAdultOnly) {
      this.previewDataContainerTarget.classList.remove("hidden");
      this.onPreviewModeChange();
    } else {
      // Hide preview data when neither current nor target requires it
      this.previewDataContainerTarget.classList.add("hidden");
    }
  }

  updateDelayedAccessExplanation() {
    if (!this.hasDelayedAccessExplanationTarget) return;
    if (!this.hasEnableDelayedAccessCheckboxTarget || !this.enableDelayedAccessCheckboxTarget.checked) return;

    const currentType = this.checkedPublishingPostingType || 'open_public';
    const targetRadio = document.querySelector('input[name="delayed_access_target_type"]:checked');
    const targetType = targetRadio ? targetRadio.value : 'membership_only';

    const publishingTypesI18n = {
      'open_public': '전체 공개',
      'membership_only': '멤버십 전용',
      'subscriber_only': '구독자 전용',
      'product': '개별 판매'
    };

    const currentLabel = publishingTypesI18n[currentType] || currentType;
    const targetLabel = publishingTypesI18n[targetType] || targetType;

    // Check if current and target are the same
    const isSameType = currentType === targetType;

    // Get the parent container to update styling - search for the rounded container
    const explanationContainer = this.delayedAccessExplanationTarget.closest('.rounded-md');

    if (isSameType) {
      // Change to warning/error style when same type
      this.delayedAccessExplanationTarget.innerHTML =
        `⚠️ 현재 공개 방식('<strong>${currentLabel}</strong>')과 전환 대상('<strong>${targetLabel}</strong>')이 동일하여 자동 전환이 무의미합니다.`;

      // Update container styling to red/warning
      if (explanationContainer) {
        explanationContainer.classList.remove('bg-blue-50', 'dark:bg-blue-900/20');
        explanationContainer.classList.add('bg-red-50', 'dark:bg-red-900/20');

        const textElement = this.delayedAccessExplanationTarget;
        textElement.classList.remove('text-blue-700', 'dark:text-blue-300');
        textElement.classList.add('text-red-700', 'dark:text-red-300');

        // Update icon color if exists
        const icon = explanationContainer.querySelector('svg');
        if (icon) {
          icon.classList.remove('text-blue-400');
          icon.classList.add('text-red-400');
        }
      }
    } else {
      // Normal state when different types
      this.delayedAccessExplanationTarget.innerHTML =
        `발행 시점에는 '<strong>${currentLabel}</strong>'로 설정되며, 설정한 기간이 지나면 자동으로 '<strong>${targetLabel}</strong>'로 전환됩니다.`;

      // Restore original styling
      if (explanationContainer) {
        explanationContainer.classList.remove('bg-red-50', 'dark:bg-red-900/20');
        explanationContainer.classList.add('bg-blue-50', 'dark:bg-blue-900/20');

        const textElement = this.delayedAccessExplanationTarget;
        textElement.classList.remove('text-red-700', 'dark:text-red-300');
        textElement.classList.add('text-blue-700', 'dark:text-blue-300');

        // Restore icon color if exists
        const icon = explanationContainer.querySelector('svg');
        if (icon) {
          icon.classList.remove('text-red-400');
          icon.classList.add('text-blue-400');
        }
      }
    }
  }

  onEmailAddressChange() {
    this.updateWarmingOption();

    if (!this.hasClickTrackingSelectTarget) return;

    const select = this.senderEmailAddressSelectTarget;
    const selectedOption = select.options[select.selectedIndex];
    const isMaily = selectedOption?.dataset.provider === "maily";

    if (isMaily) {
      this.clickTrackingSelectTarget.value = "false";
      this.clickTrackingMailyWarningTarget.classList.remove("hidden");
    } else {
      const defaultVal = this.clickTrackingSelectTarget.dataset.defaultValue;
      this.clickTrackingSelectTarget.value = defaultVal || "";
      this.clickTrackingMailyWarningTarget.classList.add("hidden");
    }
  }

  // 도메인 워밍업(분할 발송) 옵션 — 발송 이력이 적은 커스텀 도메인 + 대량 수신자일 때만 노출
  get estimatedRecipientsCount() {
    const radio = document.querySelector('input[name="post[publishing_recipient_type]"]:checked');
    if (!radio || !radio.dataset.count) return null;
    const n = parseInt(radio.dataset.count, 10);
    return isNaN(n) ? null : n;
  }

  get abTestEnabled() {
    const abTestCheckbox = document.getElementById("enable_ab_test_winner");
    return !!(abTestCheckbox && abTestCheckbox.checked);
  }

  updateWarmingOption() {
    if (!this.hasWarmingOptionPanelTarget) return;

    const panel = this.warmingOptionPanelTarget;
    const select = this.hasSenderEmailAddressSelectTarget ? this.senderEmailAddressSelectTarget : null;
    const selectedOption = select ? select.options[select.selectedIndex] : null;
    const recommended = selectedOption?.dataset.warmingRecommended === "true";
    const count = this.estimatedRecipientsCount;
    const minRecipients = parseInt(panel.dataset.warmingMinRecipients, 10) || 0;
    const show = recommended && !this.abTestEnabled && count !== null && count >= minRecipients;

    panel.classList.toggle("hidden", !show);

    if (!show) {
      // 패널이 숨겨지면 "한 번에 발송"으로 리셋 (숨겨진 채 warming 값이 제출되는 것 방지)
      this.warmingRadioTargets.forEach(r => { r.checked = r.value === "0"; });
      return;
    }

    this.updateWarmingScheduleText(count);
  }

  updateWarmingScheduleText(count) {
    if (!this.hasWarmingScheduleTextTarget) return;

    const panel = this.warmingOptionPanelTarget;
    const initial = parseInt(panel.dataset.warmingInitialSize, 10) || 500;
    const growth = parseInt(panel.dataset.warmingGrowthFactor, 10) || 2;

    const tranches = [];
    let remaining = count;
    let size = initial;
    while (remaining > 0 && tranches.length < 30) {
      const t = Math.min(size, remaining);
      tranches.push(t);
      remaining -= t;
      size *= growth;
    }

    const preview = tranches.map((t, i) => `${i + 1}일차 ${t.toLocaleString()}명`).join(" → ");
    this.warmingScheduleTextTarget.textContent = `${preview} (총 ${tranches.length}일에 걸쳐 발송)`;
  }

  connect() {
    this.onPublishingTypeBlur();
    this.toggleDelayedAccessConfig();  // Initialize delayed access panel state
    this.onEmailAddressChange();  // maily 이메일 선택 시 클릭 추적 기본값 설정
    // 스팸 체크는 사용자 액션으로만 트리거 (불필요한 API 호출 방지)

    // 폼 제출 시 발행 모드에 따른 처리
    this.element.closest("form")?.addEventListener("submit", (e) => {
      // 테스트 메일 발송은 예약 시간 검증 불필요
      if (e.submitter?.name === "send_test") return;

      // 이메일 미발송(none)은 발행 시간 설정 불필요
      if (this.checkedPublishingRecipientType === "none") return;

      // A/B 테스트 승리 발송은 제목 2개 이상 필요
      const abTestCheckbox = document.getElementById("enable_ab_test_winner");
      if (abTestCheckbox && abTestCheckbox.checked) {
        const titleInputs = this.element.closest("form").querySelectorAll('input[name="post[mail_titles][]"]');
        const filledTitles = Array.from(titleInputs).filter(input => input.value.trim() !== "");
        if (filledTitles.length < 2) {
          e.preventDefault();
          e.stopImmediatePropagation();
          createFlash({
            key: "error",
            ttl: 5,
            message: "A/B 테스트 승리 발송은 2개 이상의 제목이 필요합니다."
          });
          return false;
        }
      }

      if (this.hasSendDatetimeFieldTarget) {
        if (this.selectedSendMode === "immediate") {
          // 즉시 발행: 현재 시간을 send_datetime으로 설정
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, '0');
          const d = String(now.getDate()).padStart(2, '0');
          const h = String(now.getHours()).padStart(2, '0');
          const min = String(now.getMinutes()).padStart(2, '0');
          this.sendDatetimeFieldTarget.value = `${y}-${m}-${d} ${h}:${min}`;
        } else {
          // 예약 발행: 시간 미선택 시 제출 차단
          if (!this.sendDatetimeFieldTarget.value) {
            e.preventDefault();
            e.stopImmediatePropagation();
            createFlash({
              key: "error",
              ttl: 5,
              message: "예약 발행 시간을 선택해주세요."
            });
            this.sendDatetimeFieldTarget.scrollIntoView({ behavior: "smooth", block: "center" });
            this.sendDatetimeFieldTarget.focus();
            return false;
          }
        }
      }
    });
  }

  async checkSpam() {
    if (!this.hasSpamCheckerContainerTarget) return;

    // 초기 상태 숨기고 로딩 표시
    if (this.hasSpamCheckerIdleTarget) {
      this.spamCheckerIdleTarget.classList.add('hidden');
    }
    if (this.hasSpamCheckerLoadingTarget) {
      this.spamCheckerLoadingTarget.classList.remove('hidden');
    }

    try {
      const emailTitles = this.collectEmailTitles();

      const response = await api.post(
        `/api/${this.spaceUrlValue}/notes/${this.postExtIdValue}/check_spam`,
        { email_titles: emailTitles }
      );

      this.updateSpamCheckerUI(response.data);
    } catch (error) {
      console.error('Spam check failed:', error);
      this.showSpamCheckerError();
    }
  }

  collectEmailTitles() {
    if (!this.hasEmailTitleInputTarget) return [];

    return this.emailTitleInputTargets
      .map(input => input.value)
      .filter(v => v && v.trim().length > 0);
  }

  updateSpamCheckerUI(result) {
    if (!this.hasSpamCheckerLoadingTarget) return;

    this.spamCheckerLoadingTarget.classList.add('hidden');

    if (result.safe) {
      if (this.hasSpamCheckerSafeTarget) {
        this.spamCheckerSafeTarget.classList.remove('hidden');
      }
    } else {
      if (this.hasSpamCheckerWarningTarget) {
        this.spamCheckerWarningTarget.classList.remove('hidden');
      }
      if (this.hasSpamRiskListTarget && result.risks) {
        this.spamRiskListTarget.innerHTML = result.risks
          .map(risk => `<li>${risk.message}</li>`)
          .join('');
      }
    }
  }

  showSpamCheckerError() {
    if (this.hasSpamCheckerLoadingTarget) {
      this.spamCheckerLoadingTarget.classList.add('hidden');
    }
    if (this.hasSpamCheckerErrorTarget) {
      this.spamCheckerErrorTarget.classList.remove('hidden');
    }
  }

  toggleAbTestWinner(event) {
    if (!this.hasAbTestWinnerOptionsTarget) return;

    const enabled = event.target.checked;
    const optionsContainer = this.abTestWinnerOptionsTarget;

    if (enabled) {
      optionsContainer.classList.remove('hidden');
    } else {
      optionsContainer.classList.add('hidden');
    }

    // A/B 테스트와 도메인 워밍업 분할 발송은 동시 적용 불가
    this.updateWarmingOption();
  }
}